import {Option, none, some} from '@danwang/option';
import {
  FieldType,
  useAllAccountsQuery,
  useAllCategoriesQuery,
  useAllFieldsQuery,
  useBindingsQuery,
  useCreateRawTransactionMutation,
  useCreateTransactionMutation,
} from '@transactions/graphql';
import * as Date from 'common/date';
import * as React from 'react';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import AccountSelect from 'src/AccountSelect';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {FetchResult} from '@apollo/client';
import type {
  AccountListItemFragment,
  BasicRawTransactionFragment,
  CreateRawTransactionArgs,
  CreateRawTransactionMutation,
} from '@transactions/graphql';
import type Money from 'common/Money';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

/**
 * Payee stamped on auto-categorized adjustments — a stable name, so every
 * adjustment reports under one payee instead of scattering.
 */
export const ADJUSTMENT_PAYEE = 'Reconciliation adjustment';

export type CreateRawTransactionModalUserProps = {
  defaultAccount?: AccountListItemFragment;
  /** Prefill the account by id (e.g. when opened from an account page). */
  defaultAccountId?: string;
  /** Prefill the amount (e.g. the reconcile "fix up" offset). */
  defaultAmount?: Money;
  /** Prefill the date (defaults to today). */
  defaultDate?: DateTime;
  /** Prefill the description. */
  defaultDescription?: string;
  /**
   * Present when this create is a reconcile fix-up: the created row is
   * stamped as an adjustment for this anchor and — when the
   * reconciliationCategory binding is set — immediately consumed into a
   * categorized transaction, so the adjustment is born fully allocated
   * instead of landing in the inbox as a chore.
   */
  adjustmentForAnchorId?: string;
  onCreate?: (transaction: BasicRawTransactionFragment) => void;
};

export const CreateRawTransactionModal = (
  props: CreateRawTransactionModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const {
    onRequestClose,
    onCreate,
    defaultAccount,
    defaultAccountId,
    defaultAmount,
    defaultDate,
    defaultDescription,
    adjustmentForAnchorId,
  } = props;
  const [account, setAccount] = React.useState<Option<AccountListItemFragment>>(
    defaultAccount ? some(defaultAccount) : none,
  );
  const {data: accountsData, error: accountsError} = useAllAccountsQuery({
    skip: defaultAccount != null || defaultAccountId == null,
  });
  // Apply the id-based default at most once, so a user who clears the
  // account afterwards doesn't have it snap back when the query resolves.
  const defaultApplied = React.useRef(defaultAccount != null);
  React.useEffect(() => {
    if (defaultApplied.current) return;
    const match = accountsData?.allAccounts.find(
      (a) => a._id === defaultAccountId,
    );
    if (match) {
      defaultApplied.current = true;
      setAccount(some(match));
    }
  }, [accountsData, defaultAccountId]);
  const [amount, setAmount] = React.useState<Option<Money>>(
    defaultAmount ? some(defaultAmount) : none,
  );
  const [date, setDate] = React.useState<DateTime>(
    defaultDate ?? Date.today(),
  );
  const [description, setDescription] = React.useState(
    defaultDescription ?? '',
  );

  const [createRawTransaction, {loading: creatingRaw}] =
    useCreateRawTransactionMutation();
  const [createTransaction, {loading: consuming}] =
    useCreateTransactionMutation();
  const loading = creatingRaw || consuming;

  // Adjustment mode reads the reconciliationCategory binding to decide the
  // born-allocated consume; the queries are skipped entirely for the plain
  // create-row use of this modal.
  const isAdjustment = adjustmentForAnchorId != null;
  const {data: bindingsData, error: bindingsError} = useBindingsQuery({
    skip: !isAdjustment,
  });
  const {data: categoriesData, error: categoriesError} = useAllCategoriesQuery(
    {skip: !isAdjustment},
  );
  const {data: fieldsData, error: fieldsError} = useAllFieldsQuery({
    skip: !isAdjustment,
  });
  const boundCategoryId = bindingsData?.bindings.find(
    (binding) => binding.key === 'reconciliationCategory',
  )?.entityId;
  const adjustmentCategory = categoriesData?.allCategories.find(
    (category) => category._id === boundCategoryId,
  );
  // The consume submits no field values, and the server rejects a payment
  // missing a required enum field — with one configured, skip the consume
  // and leave the adjustment for manual categorization rather than fail.
  const requiredFields = (fieldsData?.allFields ?? []).filter(
    (field) => field.required && field.type === FieldType.Enum,
  );
  const hasRequiredFields = requiredFields.length > 0;
  const willAutoCategorize =
    isAdjustment && adjustmentCategory != null && !hasRequiredFields;
  // Unresolved config queries are not an empty configuration: submitting
  // before they answer would nondeterministically skip (or half-attempt)
  // the born-allocated consume a configured binding promises. A query is
  // settled once it has data OR a known failure — a failed config fetch
  // falls back to creating the adjustment unallocated (the degradation
  // path) instead of disabling Create forever.
  const settled = (data: unknown, error: unknown): boolean =>
    data != null || error != null;
  const adjustmentConfigReady =
    !isAdjustment ||
    (settled(bindingsData, bindingsError) &&
      settled(categoriesData, categoriesError) &&
      settled(fieldsData, fieldsError));

  // Raw transactions are denominated in their account's currency — the
  // amount input follows the selected account (¥ for a JPY wallet).
  const accountCurrency = account.map((a) => a.currency).getOrElse('USD');

  const maybeArgs: Option<CreateRawTransactionArgs> = Option.allObject({
    account,
    amount,
  }).map((obj) => {
    return {
      accountId: obj.account._id,
      amount: obj.amount.amount,
      currency: obj.account.currency,
      date: date.toISODate(),
      description,
      adjustmentForAnchorId,
    };
  });

  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    maybeArgs.forEach((args) => {
      const run = async () => {
        setError(null);
        let result: FetchResult<CreateRawTransactionMutation>;
        try {
          result = await createRawTransaction({variables: {props: args}});
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to create');
          return;
        }
        const newTransaction = result.data?.createRawTransaction;
        if (!newTransaction) return;

        let consumed = false;
        if (willAutoCategorize && adjustmentCategory) {
          // Born allocated: consume the adjustment into a categorized
          // payment right away, so it never lands in the inbox. A failure
          // here degrades instead of rolling back — the stamped row is the
          // essential part (the reconcile session needs it), and an
          // unconsumed adjustment is a to-do, not a broken state.
          try {
            await createTransaction({
              variables: {
                rawTransactions: [newTransaction._id],
                date: date.toISODate() ?? '',
                payee: ADJUSTMENT_PAYEE,
                category: adjustmentCategory.name,
              },
            });
            consumed = true;
          } catch {
            consumed = false;
          }
        }

        onRequestClose();
        onCreate?.(
          consumed
            ? // The consume also invalidates the row's FIFO-draw preview
              // (previews exist only for fully unallocated rows) — null it
              // so the stale value isn't written back into the cache.
              {...newTransaction, remainingAmount: 0, homeAmountPreview: null}
            : newTransaction,
        );
      };
      void run();
    });
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="md"
      header={'Create raw transaction'}
      footer={
        <>
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={maybeArgs.isEmpty || loading || !adjustmentConfigReady}
            form={formId}
          >
            Create
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <InputGroup label="Amount">
          <AmountInput
            value={amount}
            onChange={setAmount}
            autoFocus
            currency={accountCurrency}
          />
        </InputGroup>
        <InputGroup label="Account">
          {isAdjustment ? (
            // A fix-up belongs to the account whose anchor it closes — the
            // server rejects any other, so the picker is replaced with the
            // resolved account rather than offering choices that can only
            // fail.
            <div className="flex h-9 items-center">
              {account
                .map((selected) => (
                  <AccountInlineDisplay account={selected} />
                ))
                .getOrElse(
                  // A failed lookup must not read as an eternal loading
                  // state — the locked picker has no retry of its own, so
                  // say what happened and how to get out.
                  accountsError ? (
                    <span className="text-destructive text-sm">
                      Couldn&apos;t load the account — close and try again.
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Loading account…
                    </span>
                  ),
                )}
            </div>
          ) : (
            <AccountSelect
              selectedAccount={account}
              onSelectAccount={setAccount}
            />
          )}
        </InputGroup>
        <InputGroup label="Date">
          <DatePicker selectedDate={date} onChange={setDate} />
        </InputGroup>
        <InputGroup label="Description">
          <TextInput
            value={description}
            onChange={setDescription}
            placeholder="Description"
          />
        </InputGroup>
        {isAdjustment && (
          <div className="text-muted-foreground pt-1 text-xs">
            {willAutoCategorize && adjustmentCategory
              ? `Recorded as a reconciliation adjustment and categorized as ` +
                `"${adjustmentCategory.name}" (payee "${ADJUSTMENT_PAYEE}").`
              : hasRequiredFields
                ? `Recorded as a reconciliation adjustment. The required ` +
                  `${requiredFields
                    .map((field) => `"${field.name}"`)
                    .join(', ')} field${
                    requiredFields.length === 1 ? '' : 's'
                  } this dialog cannot collect prevent${
                    requiredFields.length === 1 ? 's' : ''
                  } automatic categorization — categorize it from the inbox.`
                : 'Recorded as a reconciliation adjustment. Set a ' +
                  'Reconciliation category in Defaults to categorize ' +
                  'adjustments automatically.'}
          </div>
        )}
        {error && <div className="text-destructive text-xs">{error}</div>}
      </form>
    </Modal>
  );
};
